/**
 * Stage-key generators for the pipeline editors (#885).
 *
 * Two contracts, because the stored keys have two shapes:
 *  - `stageKeyFromLabel`  contact (Lead) and event_contact stages: snake_case. The backend's
 *    `STAGE_KEY_RE` (`services/pipeline_definitions.py`) documents exactly this output.
 *  - `dealStageKeyFromLabel`  deal stages: the label as entered. The six legacy keys are display
 *    strings (`"Proposal Sent"`) that AI prompts, webhooks and SDK DTOs show raw (P14).
 *
 * A key is generated once, when a stage is created, and never changes afterwards.
 */

// Mirrors the backend's STAGE_KEY_MAX; a 60-character label plus a collision suffix could exceed it.
const STAGE_KEY_MAX = 60;

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/**
 * A snake_case key for a new contact / event_contact stage: lowercase, non-alphanumerics
 * collapsed to `_`, edges trimmed ("1st call" -> `1st_call`). An empty result falls back to
 * `stage_<n>` (n = how many keys exist), and a collision with `existingKeys` appends `_2`, `_3`...
 * `existingKeys` is any iterable of keys, an array or a Set.
 */
export function stageKeyFromLabel(label, existingKeys = []) {
    const taken = new Set(existingKeys);
    const base = slugify(label) || `stage_${taken.size}`;
    const fit = (suffix) => `${base.slice(0, STAGE_KEY_MAX - suffix.length).replace(/_+$/, '')}${suffix}`;
    let key = fit('');
    for (let n = 2; taken.has(key); n += 1) key = fit(`_${n}`);
    return key;
}

/**
 * The key of a new deal stage: its label as entered, trimmed and single-spaced. `null` for an
 * empty label (refused: a stage with no name has no key). Length and character rules are the
 * server's; it names the stage and the rule in its 400.
 */
export function dealStageKeyFromLabel(label) {
    const key = (label || '').replace(/\s+/g, ' ').trim();
    return key || null;
}
